import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  calc, skuCostModel, skuCellUnit, normalizeState,
  POUCH_DEFAULTS, MODES, num, money, unitMoney, intfmt,
  SHAPES, COATINGS, ADHESIVES, PROCESSES, MACH_APPLIED, JOB_TYPES, LAMINATES,
  type QuoteState, type EngineMode,
} from '../lib/quoteEngine';
import { useCustomers } from '../lib/csrSuite';
import { useSuiteQuotes, saveSuiteQuotes, nextQuoteId, type SuiteQuote } from '../lib/csrCatalog';
import { buildQuoteDocHtml } from '../lib/quoteDocHtml';
import { QUOTE_DOC_CSS } from '../lib/quoteDocCss';

// ── raw (string) form state, mirrors the CSR app's DOM getState/setState ──
type RawForm = { qty: string[]; commissionOn: boolean; [k: string]: string | string[] | boolean };

function toRaw(p: QuoteState): RawForm {
  const raw: RawForm = { qty: [], commissionOn: !!p.commissionOn };
  for (const k of Object.keys(POUCH_DEFAULTS)) {
    if (k === 'qty' || k === 'commissionOn') continue;
    const v = (p as unknown as Record<string, unknown>)[k];
    raw[k] = (v === undefined || v === null) ? '' : String(v);
  }
  const q = (p.qty || []).slice(0, 10);
  raw.qty = [...q, ...Array(Math.max(0, 10 - q.length)).fill(0)].map((x) => String(x ?? 0));
  return raw;
}

function buildState(raw: RawForm): QuoteState {
  const s: Record<string, unknown> = {};
  for (const k of Object.keys(POUCH_DEFAULTS)) {
    if (k === 'qty') { s.qty = raw.qty.map(num); continue; }
    if (k === 'commissionOn') { s.commissionOn = !!raw.commissionOn; continue; }
    const dv = (POUCH_DEFAULTS as unknown as Record<string, unknown>)[k];
    s[k] = (typeof dv === 'number') ? num(raw[k]) : String(raw[k] ?? '');
  }
  return normalizeState(s as Partial<QuoteState>);
}

export default function QuoteBuilder() {
  const [mode, setMode] = useState<EngineMode>('pouch');
  const [raw, setRaw] = useState<RawForm>(() => toRaw(POUCH_DEFAULTS));
  const [showSku, setShowSku] = useState(false);
  const [skuMax, setSkuMax] = useState(3);
  const [showQuote, setShowQuote] = useState(false);
  const [params] = useSearchParams();
  useEffect(() => {
    const company = params.get('company'), attention = params.get('attention'), salesRep = params.get('salesRep');
    if (company || attention || salesRep) setRaw((p) => ({ ...p, company: company ?? p.company, attention: attention ?? p.attention, salesRep: salesRep ?? p.salesRep }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const s = useMemo(() => buildState(raw), [raw]);
  const r = useMemo(() => calc(s), [s]);
  const skuM = useMemo(() => skuCostModel(s, r), [s, r]);

  const setF = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setRaw((p) => ({ ...p, [k]: e.target.value }));
  const setChk = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setRaw((p) => ({ ...p, [k]: e.target.checked }));
  const setQ = (i: number) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setRaw((p) => { const q = [...p.qty]; q[i] = e.target.value; return { ...p, qty: q }; });

  const switchMode = (m: EngineMode) => { setMode(m); setRaw(toRaw(MODES[m].defaults)); };
  const loadSample = () => setRaw(toRaw(MODES[mode].sample));
  const reset = () => setRaw(toRaw(MODES[mode].defaults));

  // derived
  const priced = r.rows.filter((x) => x.q > 0);
  const best = priced.reduce<null | typeof r.rows[number]>((b, x) => (!b || x.mfxUnit < b.mfxUnit ? x : b), null);
  const bd = best || r.rows.find((x) => x.q > 0) || null;

  const warns: string[] = [];
  if (s.zipperState === 'tbd') warns.push('Zipper cost is TBD — the price EXCLUDES the zipper. Confirm the cost before quoting.');
  if (s.sizeAcross <= 0) warns.push('Size Across is 0 — web width and pricing won’t be meaningful.');
  if (s.nAcross < 1) warns.push('# Across should be at least 1.');
  if (s.msiCost <= 0) warns.push('MSI cost is 0 — stock cost will be $0.');
  if (r.rows.every((x) => x.q <= 0)) warns.push('No quantities entered yet.');

  const { customers } = useCustomers();
  const { quotes: savedQuotes } = useSuiteQuotes();
  const [qStatus, setQStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [qMsg, setQMsg] = useState('');
  const [loadedQuoteId, setLoadedQuoteId] = useState('');
  const cust = useMemo(() => customers.find((c) => c.n === s.company), [customers, s.company]);
  const quoteHtml = useMemo(() => buildQuoteDocHtml(s, r, mode, cust, { skuMode: showSku, skuMax }), [s, r, mode, cust, showSku, skuMax]);

  async function saveQuote() {
    setQStatus('saving');
    try {
      const _n2 = new Date();
      const today2 = _n2.getFullYear() + '-' + String(_n2.getMonth() + 1).padStart(2, '0') + '-' + String(_n2.getDate()).padStart(2, '0');
      const who = s.estimator || 'Team';
      let items: SuiteQuote[];
      if (loadedQuoteId && savedQuotes.some((x) => x.id === loadedQuoteId)) {
        items = savedQuotes.map((x) => (x.id === loadedQuoteId ? {
          ...x, quoteNo: s.quoteNo || x.quoteNo, client: s.company || x.client,
          product: s.desc || x.product, mode, value: best ? best.mfxTotal : x.value,
          qtys: priced.map((p2) => p2.q), updated: today2, snapshot: { s },
          history: [...(((x.history as { ts: string; who: string; act: string }[]) || [])), { ts: new Date().toISOString(), who, act: 'Updated in unified Quote Builder' }],
        } : x));
      } else {
        const id = await nextQuoteId();
        const item: SuiteQuote = {
          id, quoteNo: s.quoteNo || id, client: s.company || '—',
          product: s.desc || `${s.sizeAcross}x${s.sizeAround} ${s.shape}`, mode,
          value: best ? best.mfxTotal : 0, qtys: priced.map((p2) => p2.q),
          status: 'In Progress', assignee: who, createdBy: who,
          date: today2, updated: today2, po: '', lossReason: '', requestId: '',
          snapshot: { s }, history: [{ ts: new Date().toISOString(), who, act: 'Quote created in unified Quote Builder' }],
        };
        items = [item, ...savedQuotes];
        setLoadedQuoteId(id);
      }
      await saveSuiteQuotes(items);
      setQStatus('saved'); setQMsg('Saved to Quotes');
      setTimeout(() => setQStatus('idle'), 1800);
    } catch (e) {
      setQStatus('error');
      setQMsg((e as Error)?.message?.includes('permission') ? 'Sign in with your Microflex account to save.' : ((e as Error)?.message || 'Save failed'));
    }
  }
  function loadQuote(id: string) {
    setLoadedQuoteId(id);
    const item = savedQuotes.find((x) => x.id === id);
    const snap = item?.snapshot as { s?: Partial<QuoteState> } | null | undefined;
    if (snap && snap.s) { setRaw(toRaw(normalizeState(snap.s))); setShowQuote(true); }
  }

  const rawStr = (k: string) => String(raw[k] ?? '');
  const N = (label: string, k: string, step = 'any', unit?: string) => (
    <label className="qb-f"><span className="qb-l">{label}{unit && <em> {unit}</em>}</span>
      <input type="number" step={step} value={rawStr(k)} onChange={setF(k)} /></label>
  );
  const S = (label: string, k: string, opts: string[], allowNone?: boolean) => (
    <label className="qb-f"><span className="qb-l">{label}</span>
      <select value={rawStr(k)} onChange={setF(k)}>
        {allowNone && <option value="">— None —</option>}
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select></label>
  );
  const T = (label: string, k: string) => (
    <label className="qb-f"><span className="qb-l">{label}</span>
      <input type="text" value={rawStr(k)} onChange={setF(k)} /></label>
  );

  return (
    <>
      <style>{qbCss}</style>

      <div className="page-head">
        <div>
          <div className="page-title">Quote Builder <span className="qb-beta">in-app · live engine</span></div>
          <div className="page-sub">The native MicroFlex estimator — same math as the estimating suite, validated to the penny. {MODES[mode].sub}</div>
        </div>
        <div className="seg">
          {(['pouch', 'master'] as EngineMode[]).map((m) => (
            <button key={m} className={mode === m ? 'on' : ''} onClick={() => switchMode(m)}>{MODES[m].label}</button>
          ))}
        </div>
      </div>

      {/* KPI band */}
      <div className="stat-band">
        <div className="stat hero-stat"><div className="stat-v">{best ? unitMoney(best.mfxUnit) : '—'}</div><div className="stat-l">Best Unit Price</div><div className="stat-note">{best ? `at ${intfmt(best.q)} units` : 'enter a quantity'}</div></div>
        <div className="stat"><div className="stat-v">{(Math.round(r.webWidth * 1000) / 1000)}</div><div className="stat-l">Web Width (in)</div><div className="stat-note">repeat {(+r.repeat.toFixed(3))}</div></div>
        <div className="stat"><div className="stat-v">{r.plates}</div><div className="stat-l">Plates</div><div className="stat-note">{r.printCyl} print cyl</div></div>
        <div className="stat"><div className="stat-v">{priced.length}</div><div className="stat-l">Priced Tiers</div><div className="stat-note">of 10</div></div>
        <div className="stat"><div className="stat-v">{MODES[mode].lead}d</div><div className="stat-l">Lead Time</div><div className="stat-note">business days</div></div>
      </div>

      <div className="qb-actions">
        <button className="btn small" onClick={loadSample}>Load sample ({MODES[mode].label})</button>
        <button className="btn small" onClick={reset}>Reset to defaults</button>
        <label className="qb-toggle"><input type="checkbox" checked={showSku} onChange={(e) => setShowSku(e.target.checked)} /> SKU × Qty matrix</label>
        <button className="btn small primary" onClick={() => setShowQuote((v) => !v)}>{showQuote ? 'Hide' : 'Preview'} customer quote</button>
        {savedQuotes.length > 0 && (
          <select value={loadedQuoteId} onChange={(e) => e.target.value && loadQuote(e.target.value)} style={{ maxWidth: 240 }}>
            <option value="">Open saved quote…</option>
            {savedQuotes.map((x) => <option key={x.id} value={x.id}>{x.id} · {x.client}</option>)}
          </select>
        )}
      </div>

      {warns.length > 0 && <div className="qb-warn">⚠ {warns.join('  ·  ')}</div>}

      <div className="qb-cols">
        {/* LEFT: inputs */}
        <div className="qb-left">
          <div className="card"><div className="card-header"><h3>Job</h3></div><div className="qb-grid">
            {T('Company', 'company')}{T('Attention', 'attention')}{T('Quote #', 'quoteNo')}
            {T('Estimator', 'estimator')}{T('Sales Rep', 'salesRep')}{S('Job Type', 'jobType', JOB_TYPES)}
            <label className="qb-f qb-span2"><span className="qb-l">Description</span><input type="text" value={rawStr('desc')} onChange={setF('desc')} /></label>
            {T('Freight terms', 'freight')}
          </div></div>

          <div className="card"><div className="card-header"><h3>Specs</h3></div><div className="qb-grid">
            {S('Shape', 'shape', SHAPES)}
            {N('Size Across', 'sizeAcross', 'any', 'in')}{N('Size Around', 'sizeAround', 'any', 'in')}
            {N('# Across', 'nAcross', '1')}{N('# Around', 'nAround', '1')}
            {N('Gap Across', 'gapAcross', 'any')}{N('Gap Around', 'gapAround', 'any')}
            {N('Offcut', 'offcut', 'any')}{N('Corner Radius', 'cornerRadius', 'any')}
            {N('Colors', 'colors', '1')}{N('Copies', 'copies', '1')}{N('Common Plates', 'commonPlates', '1')}
            {S('Process', 'process', PROCESSES)}{S('Machine Applied', 'machApplied', MACH_APPLIED)}
          </div></div>

          <div className="card"><div className="card-header"><h3>Materials</h3></div><div className="qb-grid">
            {N('MSI Cost', 'msiCost', 'any', '$/msi')}
            {S('Coating', 'coating', COATINGS)}
            {S('Laminate', 'laminate', LAMINATES.map((l) => l.name), true)}
            {S('Adhesive', 'adhesive', ADHESIVES)}
            {T('Face Stock', 'faceStock')}{T('Die', 'die')}
          </div></div>

          <div className="card"><div className="card-header"><h3>Cost Inputs</h3></div><div className="qb-grid">
            {N('Material Set-Up', 'matSetup', '1', '$')}{N('Stock Margin', 'stockMargin', 'any', '×')}
            {N('Microflex Mark-Up', 'mfxMU', '0.01', '×')}{N('PPC Mark-Up', 'ppcMU', '0.01', '×')}
            {N('Make-Ready Hours', 'mrHours', '0.5')}{N('Make-Ready $/hr', 'mrRate', '1')}
            {N('Clean-Up Hours', 'cuHours', '0.5')}{N('Clean-Up $/hr', 'cuRate', '1')}
            {N('Cost / Plate', 'costPerPlate', '1')}{N('# Plate Changes', 'numPlates', '1')}
            {N('Cost / Color Change', 'costPerChange', '1')}{N('# Color Changes', 'numChanges', '1')}
          </div></div>

          <div className="card"><div className="card-header"><h3>Zipper</h3></div><div className="qb-grid">
            {N('Zipper Cost', 'zipperCost', 'any', '$')}
            <label className="qb-f"><span className="qb-l">Zipper Basis</span>
              <select value={rawStr('zipperBasis')} onChange={setF('zipperBasis')}>
                <option value="M">per 1,000</option><option value="U">per unit</option><option value="F">flat / job</option>
              </select></label>
            <label className="qb-f"><span className="qb-l">Zipper Status</span>
              <select value={rawStr('zipperState')} onChange={setF('zipperState')}>
                <option value="na">N/A — no zipper</option><option value="confirmed">Confirmed</option>
                <option value="estimated">Estimated</option><option value="tbd">TBD — not priced</option>
              </select></label>
          </div></div>

          <div className="card"><div className="card-header"><h3>Commission <span className="qb-hint">(off = no change)</span></h3></div><div className="qb-grid">
            <label className="qb-f"><span className="qb-l">Include commission</span>
              <label className="qb-check"><input type="checkbox" checked={!!raw.commissionOn} onChange={setChk('commissionOn')} /> Add rep commission to price</label></label>
            {N('Commission %', 'commissionPct', '0.5', 'to rep')}
          </div></div>

          <div className="card"><div className="card-header"><h3>Quantities <span className="qb-hint">up to 10 tiers</span></h3></div>
            <div className="qb-qty">
              {raw.qty.map((v, i) => (
                <label key={i} className="qb-f"><span className="qb-l">#{i + 1}</span>
                  <input type="number" step="1" value={v} onChange={setQ(i)} /></label>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT: results */}
        <div className="qb-right">
          <div className="card qb-sticky"><div className="card-header"><h3>Pricing</h3><span className="qb-hint">Microflex price</span></div>
            <div className="table-wrap"><table>
              <thead><tr><th>Qty</th><th>Net Ft</th><th>Unit</th><th>Total</th></tr></thead>
              <tbody>
                {priced.length === 0 && <tr><td colSpan={4} className="qb-empty">Enter at least one quantity.</td></tr>}
                {priced.map((row) => (
                  <tr key={row.q} className={best && row.q === best.q ? 'qb-best' : ''}>
                    <td>{intfmt(row.q)}</td><td>{intfmt(row.netFt)}</td>
                    <td className="qb-unit">{unitMoney(row.mfxUnit)}</td><td className="qb-tot">{money(row.mfxTotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>

            {bd && (
              <div className="qb-bd">
                <div className="qb-bd-h">Cost trace @ {intfmt(bd.q)} units</div>
                <Line l="Stock cost (web × ft × 0.012 × MSI)" v={money(bd.stockCost)} sub />
                <Line l={`× Stock margin (${s.stockMargin}×) = Quantity break`} v={money(bd.qtyBreak)} />
                <Line l="Plate changes" v={money(r.plateChanges)} sub />
                <Line l="Color changes" v={money(r.colorChanges)} sub />
                <Line l={`Make-ready (${s.mrHours}h × ${money(s.mrRate)})`} v={money(r.makeReady)} sub />
                <Line l={`Clean-up (${s.cuHours}h × ${money(s.cuRate)})`} v={money(r.cleanUp)} sub />
                {bd.zipper > 0 && <Line l={`Zipper${s.zipperState === 'estimated' ? ' (est.)' : ''}`} v={money(bd.zipper)} sub />}
                <Line l={<b>Base job cost</b>} v={<b>{money(bd.base)}</b>} />
                {s.mfxMU > 0 && <Line l={`+ Mark-Up (${(s.mfxMU * 100).toFixed(0)}%)`} v={money(bd.mfxPreComm - bd.base)} />}
                {bd.commission > 0 && <Line l={`+ Rep commission (${num(rawStr('commissionPct'))}% grossed up)`} v={money(bd.commission)} />}
                <Line l={<b>Extended total</b>} v={<b>{money(bd.mfxTotal)}</b>} big />
              </div>
            )}
          </div>

          {showSku && (
            <div className="card"><div className="card-header"><h3>SKU × Quantity matrix</h3>
              <label className="qb-f" style={{ maxWidth: 130 }}><span className="qb-l">Max SKUs</span>
                <input type="number" min={1} max={10} value={skuMax} onChange={(e) => setSkuMax(Math.max(1, Math.min(10, num(e.target.value) || 1)))} /></label>
            </div>
              {priced.length === 0 ? <div className="qb-empty">Enter a quantity to build the matrix.</div> : (
                <div className="table-wrap"><table>
                  <thead><tr><th>Qty</th>{Array.from({ length: skuMax }, (_, j) => <th key={j}>{j + 1} SKU{j ? 's' : ''}</th>)}</tr></thead>
                  <tbody>
                    {priced.map((row) => (
                      <tr key={row.q}><td>{intfmt(row.q)}</td>
                        {Array.from({ length: skuMax }, (_, j) => <td key={j} className="qb-unit">{unitMoney(skuCellUnit(j + 1, row.q, skuM))}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              )}
            </div>
          )}

          {showQuote && (
            <div className="card" style={{ padding: 14 }}>
              <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                <button className="btn small" style={{ width: 'auto', padding: '8px 14px' }} onClick={() => window.print()}>🖨 Print / Save PDF</button>
                <button className="btn small primary" style={{ width: 'auto', padding: '8px 14px' }} onClick={saveQuote} disabled={qStatus === 'saving'}>
                  {qStatus === 'saving' ? 'Saving…' : qStatus === 'saved' ? '✔ ' + qMsg : qStatus === 'error' ? '⚠ ' + qMsg : '💾 Save to Quotes'}
                </button>
                {loadedQuoteId && <span className="qb-hint" style={{ alignSelf: 'center' }}>editing {loadedQuoteId}</span>}
              </div>
              <style>{QUOTE_DOC_CSS}</style>
              <div className="quote mfqwrap" dangerouslySetInnerHTML={{ __html: quoteHtml }} />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function Line({ l, v, sub, big }: { l: React.ReactNode; v: React.ReactNode; sub?: boolean; big?: boolean }) {
  return <div className={`qb-line${sub ? ' sub' : ''}${big ? ' big' : ''}`}><span>{l}</span><span>{v}</span></div>;
}

const qbCss = `
.qb-beta{font-size:11px;font-weight:700;color:var(--ac);background:rgba(0,168,207,.14);padding:2px 8px;border-radius:999px;margin-left:8px;vertical-align:middle}
.qb-hint,.qb-l em{color:var(--tx3);font-weight:500;font-style:normal;font-size:11px}
.qb-actions{display:flex;gap:10px;align-items:center;flex-wrap:wrap;margin:14px 0 6px}
.qb-actions .btn{width:auto;padding:8px 14px;font-size:13px}
.qb-toggle,.qb-check{display:flex;align-items:center;gap:8px;font-size:13px;color:var(--tx2);font-weight:600}
.qb-toggle input,.qb-check input{width:auto;margin:0}
.qb-warn{background:rgba(234,179,8,.12);border:1px solid rgba(234,179,8,.4);color:var(--yl);border-radius:10px;padding:10px 14px;font-size:13px;margin:10px 0}
.qb-cols{display:grid;grid-template-columns:1.15fr .85fr;gap:16px;align-items:start;margin-top:8px}
@media(max-width:1050px){.qb-cols{grid-template-columns:1fr}}
.qb-left{display:flex;flex-direction:column;gap:14px}
.qb-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px 14px;padding:14px}
@media(max-width:640px){.qb-grid{grid-template-columns:repeat(2,1fr)}}
.qb-qty{display:grid;grid-template-columns:repeat(5,1fr);gap:10px 12px;padding:14px}
@media(max-width:640px){.qb-qty{grid-template-columns:repeat(2,1fr)}}
.qb-f{display:flex;flex-direction:column;gap:5px}
.qb-span2{grid-column:span 2}
.qb-l{font-size:11px;color:var(--tx3);font-weight:600;letter-spacing:.02em}
.qb-sticky{position:sticky;top:12px}
.qb-best{background:rgba(0,168,207,.08)}
.qb-unit{font-variant-numeric:tabular-nums;font-weight:700;color:var(--ac)}
.qb-tot{font-variant-numeric:tabular-nums}
.qb-empty{text-align:center;color:var(--tx3);padding:18px}
.qb-bd{border-top:1px solid var(--bdr);margin-top:6px;padding:12px 14px}
.qb-bd-h{font-size:12px;font-weight:700;color:var(--tx2);margin-bottom:8px;text-transform:uppercase;letter-spacing:.04em}
.qb-line{display:flex;justify-content:space-between;gap:12px;padding:4px 0;font-size:13px;font-variant-numeric:tabular-nums}
.qb-line.sub{color:var(--tx3);font-size:12px;padding-left:10px}
.qb-line.big{border-top:1px solid var(--bdr);margin-top:6px;padding-top:8px;font-size:15px}
.qb-onetime{padding:8px 0;font-size:13px;color:var(--tx2)}
`;
